// ===========================================================================
// Denials Management Service (Módulo G — BillingV2)
//
// TODO BACKEND: métodos con datos fake (services/fakeData.js) y latencia
// simulada. Cuando el schema `billing` exista (ingesta EDI 835), reemplazar
// por axios real (src/utils/Apis/MCMCore/axios.js) contra /api/billing/*.
// ===========================================================================

import { FAKE_DENIALS, FAKE_RECOVERY_SUMMARY } from "./fakeData";
import { DENIAL_STATUS } from "../common/constants";

const SIMULATED_LATENCY_MS = 350;
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Obtiene la worklist de denials priorizada por monto.
 *
 * Endpoint futuro:
 * GET /api/billing/denials?status=open&payerId={id}&sort=amount_desc
 *
 * Respuesta esperada (200): array de billing.TAB_DENIALS con CARC/RARC
 * clasificado, monto, deadline de apelación y estado del workflow.
 *
 * @returns {Promise<Object[]>}
 */
export const getDenials = async () => {
  await delay(SIMULATED_LATENCY_MS);
  // Orden: monto descendente (cola priorizada por $ en disputa).
  return FAKE_DENIALS.map((d) => ({ ...d })).sort((a, b) => b.amount - a.amount);
};

/**
 * Registra la apelación de un denial.
 *
 * Endpoint futuro:
 * POST /api/billing/denials/{claimId}/appeal
 * Body: { appealLevel: 1, notes: string, filedBy: number }
 *
 * Respuesta esperada (200): { "result": 0, "message": "Appeal filed", "appealId": 123 }
 * Posibles errores (400): { "result": 1, "message": "Appeal deadline expired" }
 *
 * @param {string} claimId
 * @returns {Promise<{result: number, status: string}>}
 */
export const fileAppeal = async (claimId) => {
  await delay(200);
  // Local-only hasta que exista backend.
  return { result: 0, status: DENIAL_STATUS.IN_APPEAL, claimId };
};

/**
 * Marca el outcome de un denial (recuperado o write-off).
 *
 * Endpoint futuro:
 * PUT /api/billing/denials/{claimId}/outcome
 * Body: { outcome: "recovered" | "written_off", amountRecovered?: number }
 *
 * Respuesta esperada (200): { "result": 0, "message": "Outcome recorded" }
 *
 * @param {string} claimId
 * @param {"recovered"|"written_off"} outcome
 * @returns {Promise<{result: number, status: string}>}
 */
export const recordOutcome = async (claimId, outcome) => {
  await delay(200);
  return { result: 0, status: outcome, claimId };
};

/**
 * Métricas para la Signal Bar a partir de los denials cargados.
 * (Backend futuro: GET /api/billing/denials/summary?period=YYYY-MM)
 *
 * @param {Object[]} denials
 * @returns {{deniedMtd: number, recoveryRate: number, avgDaysToAppeal: number, topCarc: string}}
 */
export const buildDenialSignals = (denials = []) => {
  const carcTotals = denials.reduce((acc, d) => {
    acc[d.carc] = (acc[d.carc] || 0) + d.amount;
    return acc;
  }, {});
  const topCarc =
    Object.entries(carcTotals).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";
  return {
    deniedMtd: FAKE_RECOVERY_SUMMARY.deniedMtd,
    recoveryRate: Math.round(
      (FAKE_RECOVERY_SUMMARY.recoveredMtd / FAKE_RECOVERY_SUMMARY.deniedMtd) * 100
    ),
    avgDaysToAppeal: FAKE_RECOVERY_SUMMARY.avgDaysToAppeal,
    topCarc,
  };
};
